import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Spinner from '../components/Spinner';
import { db } from '../config/firebase';
import { doc, getDoc, collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { 
  formatCurrency, 
  STUDENT_STAGES,
  COMMISSION_STATUS 
} from '../utils/partnerUtils';

const PartnerDashboardPage = ({ darkMode }) => {
  const [partnerData, setPartnerData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addReferralModal, setAddReferralModal] = useState(false);
  const [newReferral, setNewReferral] = useState({
    studentName: '',
    studentPhone: '',
    studentEmail: '',
    notes: ''
  });
  const navigate = useNavigate();

  // Format date function (same as AdminPartnersManagementPage)
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format date in the required format: "4th July, 2025"
  const formatDateWithOrdinal = (date) => {
    if (!date) return 'N/A';
    
    const dateObj = new Date(date);
    const day = dateObj.getDate();
    const month = dateObj.toLocaleDateString('en-US', { month: 'long' });
    const year = dateObj.getFullYear();
    
    // Add ordinal suffix
    const ordinal = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${day}${ordinal(day)} ${month}, ${year}`;
  };

  // Format date and time in the required format: "5th July, 2025, 06:06:12 pm"
  const formatDateTimeWithOrdinal = (date) => {
    if (!date) return 'N/A';
    
    const dateObj = new Date(date);
    const day = dateObj.getDate();
    const month = dateObj.toLocaleDateString('en-US', { month: 'long' });
    const year = dateObj.getFullYear();
    const time = dateObj.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: true 
    });
    
    // Add ordinal suffix
    const ordinal = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${day}${ordinal(day)} ${month}, ${year}, ${time.toLowerCase()}`;
  };

  // Mask payment details for display
  const maskPaymentDetails = (paymentAccount) => {
    if (!paymentAccount) return null;
    
    const details = [];
    
    if (paymentAccount.hasUpi && paymentAccount.upiId) {
      const upiId = paymentAccount.upiId;
      const atIndex = upiId.indexOf('@');
      if (atIndex > 0) {
        const username = upiId.substring(0, atIndex);
        const domain = upiId.substring(atIndex);
        const maskedUsername = username.length > 4 
          ? username.substring(0, 3) + 'x'.repeat(username.length - 3)
          : 'x'.repeat(username.length);
        details.push(`UPI ID: ${maskedUsername}${domain}`);
      }
    }
    
    if (paymentAccount.hasBank && paymentAccount.bankAccount?.accountNumber) {
      const accountNumber = paymentAccount.bankAccount.accountNumber;
      const masked = accountNumber.length > 8
        ? accountNumber.substring(0, 4) + 'x'.repeat(accountNumber.length - 8) + accountNumber.substring(accountNumber.length - 4)
        : 'x'.repeat(accountNumber.length);
      details.push(`Bank account: ${masked}`);
    }
    
    return details;
  };

  // Single useEffect to handle authentication, partner data, and real-time referrals
  useEffect(() => {
    const partnerSession = sessionStorage.getItem('partnerSession');
    if (!partnerSession) {
      navigate('/partner-login');
      return;
    }

    const session = JSON.parse(partnerSession);
    const partnerId = session.partnerId;

    console.log('Setting up partner dashboard for:', partnerId);
    
    let unsubscribe = null;
    let timeoutId = null;
    
    const initializePartnerDashboard = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Set a timeout to prevent endless loading
        timeoutId = setTimeout(() => {
          if (loading) {
            console.log('Dashboard loading timeout - setting loading to false');
            setLoading(false);
            setError('Loading timeout. Please try refreshing the page.');
          }
        }, 10000); // 10 second timeout
        
        console.log('Fetching partner document for:', partnerId);
        
        // First, verify partner exists and fetch partner data
        const partnerDoc = await getDoc(doc(db, 'partners', partnerId));
        
        if (!partnerDoc.exists()) {
          console.log('Partner document not found for:', partnerId);
          setError('Partner not found. Please contact admin.');
          setLoading(false);
          return;
        }
        
        const partnerInfo = partnerDoc.data();
        console.log('Partner data loaded:', partnerInfo);
        
        // Set basic partner data
        setPartnerData({
          partnerId: partnerId,
          partnerName: partnerInfo.partnerName,
          partnerOVID: partnerInfo.partnerID, // OV01, OV02, etc.
          email: partnerInfo.email,
          institutionName: partnerInfo.institutionName,
          address: partnerInfo.address,
          registeredAt: partnerInfo.registeredAt?.toDate?.() || new Date(),
          paymentAccount: partnerInfo.paymentAccount || null, // Include payment account details
          totalStudentsReferred: 0,
          studentsWithFlightsBooked: 0,
          totalCommissionEarned: 0,
          pendingCommission: 0,
        });
        
        // Now set up real-time listener for referrals
        console.log('Setting up referrals listener for:', partnerId);
        const referralsRef = collection(db, 'partners', partnerId, 'referrals');
        
        unsubscribe = onSnapshot(referralsRef, async (snapshot) => {
          console.log('Referrals snapshot updated, count:', snapshot.size);
          
          // Clear timeout since we got data
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          
          const partnerReferrals = [];
          
          // Process each referral and fetch student photos
          for (const docSnapshot of snapshot.docs) {
            const referralData = docSnapshot.data();
            
            // Try to fetch student photo from admin students collection
            let studentPhoto = null;
            try {
              // Query students collection to find matching student by email or phone
              const studentsRef = collection(db, 'students');
              const studentQuery = query(
                studentsRef,
                where('email', '==', referralData.studentEmail)
              );
              const studentQuerySnapshot = await getDocs(studentQuery);
              
              if (!studentQuerySnapshot.empty) {
                const studentDoc = studentQuerySnapshot.docs[0];
                const studentData = studentDoc.data();
                studentPhoto = studentData.files?.photo?.url || null;
              }
            } catch (error) {
              console.log('Could not fetch student photo for:', referralData.studentEmail, error);
            }
            
            partnerReferrals.push({
              id: docSnapshot.id,
              studentName: referralData.studentName,
              studentPhone: referralData.studentPhone,
              studentEmail: referralData.studentEmail,
              studentPhoto: studentPhoto, // Add student photo
              university: referralData.university,
              referredAt: referralData.referredAt,
              stages: referralData.stages || {
                referred: true,
                seatBooked: false,
                testProvided: false,
                invitationLetter: false,
                admissionConfirmed: false,
                visaApproved: false,
                flightBooked: false
              },
              commissionStatus: calculateCommissionStatus(referralData.stages || {}),
              commissionAmount: calculateCommissionAmount(referralData.stages || {}),
              commissionEarnedAt: referralData.stages?.flightBooked ? (referralData.flightBookedAt || new Date().toISOString()) : null
            });
          }
          
          setReferrals(partnerReferrals);
          
          // Update partner data statistics
          const totalStudentsReferred = partnerReferrals.length;
          const studentsWithFlightsBooked = partnerReferrals.filter(r => r.stages.flightBooked).length;
          const totalCommissionEarned = partnerReferrals
            .filter(r => r.stages.flightBooked)
            .reduce((sum, r) => sum + 20000, 0); // ₹20,000 per student with flight booked
          const pendingCommission = 0; // No pending commission - only paid when flight is booked
          
          setPartnerData(prev => prev ? ({
            ...prev,
            totalStudentsReferred,
            studentsWithFlightsBooked,
            totalCommissionEarned,
            pendingCommission,
          }) : prev);
          
          setLoading(false);
        }, (error) => {
          console.error('Error in referrals listener:', error);
          
          // Clear timeout
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          
          // Only show error if it's not a permission error (which is normal for empty collections)
          if (error.code !== 'permission-denied') {
            setError('Failed to load referrals: ' + error.message);
          } else {
            console.log('Permission denied - likely empty referrals collection, continuing...');
          }
          setLoading(false);
        });
        
      } catch (error) {
        console.error('Error initializing partner dashboard:', error);
        
        // Clear timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        
        setError('Failed to load partner data: ' + error.message);
        setLoading(false);
      }
    };
    
    initializePartnerDashboard();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (unsubscribe) {
        console.log('Cleaning up referrals listener');
        unsubscribe();
      }
    };
  }, [navigate]);

  // Helper function to calculate commission status based on stages
  const calculateCommissionStatus = (stages) => {
    if (stages.flightBooked) {
      return 'Will be paid within 5-7 working days';
    } else if (stages.referred) {
      return COMMISSION_STATUS.IN_PROGRESS;
    }
    return 'Pending';
  };

  // Helper function to calculate commission amount
  const calculateCommissionAmount = (stages) => {
    if (stages.flightBooked) {
      return 20000; // ₹20,000 for completed referral (flight booked & departed)
    }
    return 0; // No commission until flight is booked and departed
  };

  const handleLogout = () => {
    sessionStorage.removeItem('partnerSession');
    navigate('/partner-login');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Will be paid within 5-7 working days': 
        return 'bg-green-100 text-green-800 border-green-300';
      case COMMISSION_STATUS.IN_PROGRESS: 
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default: 
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const StatusProgressBar = ({ stages }) => {
    const stageLabels = {
      referred: 'Referred',
      seatBooked: 'Seat Booked',
      testProvided: 'Test Provided',
      invitationLetter: 'Invitation Letter',
      admissionConfirmed: 'Admission Confirmed',
      visaApproved: 'Visa Approved',
      flightBooked: 'Flight Booked & Departed'
    };

    return (
      <div className="space-y-2 sm:space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-1.5 sm:gap-2">
          {Object.entries(stageLabels).map(([stageKey, stageLabel], index) => {
            const isCompleted = stages[stageKey] || false;
            
            return (
              <div
                key={stageKey}
                className={`group px-2 py-1.5 sm:px-3 sm:py-2 rounded-lg text-xs sm:text-sm font-medium border transition-all duration-300 backdrop-blur-sm ${
                  isCompleted 
                    ? `${darkMode ? 'bg-green-600/30 text-green-300 border-green-500/50' : 'bg-green-100/80 text-green-800 border-green-300/60'} shadow-md` 
                    : `${darkMode ? 'bg-gray-600/20 text-gray-400 border-gray-500/30' : 'bg-gray-100/60 text-gray-600 border-gray-300/50'} shadow-sm`
                }`}
              >
                <span className="flex items-center gap-1.5 sm:gap-2">
                  <span className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    isCompleted 
                      ? `${darkMode ? 'bg-green-500/50' : 'bg-green-500/80'} text-white` 
                      : `${darkMode ? 'bg-gray-500/30' : 'bg-gray-400/60'} text-gray-600`
                  } backdrop-blur-sm`}>
                    {isCompleted ? '✓' : index + 1}
                  </span>
                  <span className="truncate text-xs sm:text-sm">{stageLabel}</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        darkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen relative ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'}`}>
      {/* Background Blur Elements for Glassmorphism */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
        <div className="absolute top-60 right-20 w-80 h-80 bg-gradient-to-r from-pink-400/20 to-orange-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-1/3 w-64 h-64 bg-gradient-to-r from-green-400/20 to-blue-400/20 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <div className={`border-b ${darkMode ? 'border-gray-700/50 bg-gray-800/80' : 'border-white/20 bg-white/10'} backdrop-blur-xl shadow-lg relative z-10`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex flex-col py-4 sm:py-6 gap-3 sm:gap-4 sm:flex-row sm:justify-between sm:items-center">
            <div className="flex-1 min-w-0">
              <h1 className={`text-lg sm:text-xl lg:text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'} break-words bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent leading-tight`}>
                Welcome, {partnerData?.partnerName || 'Partner'}
              </h1>
              <div className={`text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} space-y-1 sm:space-y-1.5 mt-2 sm:mt-3`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <span className="font-medium">Partner ID:</span> 
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100/80 text-blue-700'} backdrop-blur-sm w-fit`}>
                    {partnerData?.partnerOVID || partnerData?.partnerId}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <span className="font-medium">Email:</span> 
                  <span className="break-all sm:break-normal text-xs sm:text-sm">{partnerData?.email}</span>
                </div>
                {partnerData?.institutionName && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <span className="font-medium">Institution:</span> 
                    <span className="break-words text-xs sm:text-sm">{partnerData.institutionName}</span>
                  </div>
                )}
                {partnerData?.registeredAt && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <span className="font-medium">Member since:</span> 
                    <span className="break-words text-xs sm:text-sm">{formatDate(partnerData.registeredAt)}</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={handleLogout}
              className={`px-3 py-2 sm:px-6 sm:py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 transform backdrop-blur-sm ${
                darkMode 
                  ? 'bg-red-600/20 text-red-300 border border-red-500/30 hover:bg-red-600/30' 
                  : 'bg-red-500/20 text-red-700 border border-red-300/50 hover:bg-red-500/30'
              } shadow-lg whitespace-nowrap self-start sm:self-center text-sm sm:text-base`}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-10 relative z-10">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-10">
          <div className={`group p-6 sm:p-8 rounded-2xl ${
            darkMode 
              ? 'bg-gray-800/40 border border-gray-700/50' 
              : 'bg-white/30 border border-white/20'
          } backdrop-blur-xl shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-105 transform`}>
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl ${darkMode ? 'bg-blue-600/20' : 'bg-blue-500/20'} backdrop-blur-sm`}>
                <svg className={`w-6 h-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
            <h3 className={`text-xs sm:text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-3 leading-tight`}>
              Total Students Referred
            </h3>
            <p className={`text-3xl sm:text-4xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'} break-words bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent`}>
              {partnerData?.totalStudentsReferred}
            </p>
          </div>

          <div className={`group p-6 sm:p-8 rounded-2xl ${
            darkMode 
              ? 'bg-gray-800/40 border border-gray-700/50' 
              : 'bg-white/30 border border-white/20'
          } backdrop-blur-xl shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-105 transform`}>
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl ${darkMode ? 'bg-green-600/20' : 'bg-green-500/20'} backdrop-blur-sm`}>
                <svg className={`w-6 h-6 ${darkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
              </div>
            </div>
            <h3 className={`text-xs sm:text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-3 leading-tight`}>
              Students with Flights Booked
            </h3>
            <p className={`text-3xl sm:text-4xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'} break-words bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent`}>
              {partnerData?.studentsWithFlightsBooked}
            </p>
          </div>

          <div className={`group p-6 sm:p-8 rounded-2xl ${
            darkMode 
              ? 'bg-gray-800/40 border border-gray-700/50' 
              : 'bg-white/30 border border-white/20'
          } backdrop-blur-xl shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-105 transform`}>
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl ${darkMode ? 'bg-purple-600/20' : 'bg-purple-500/20'} backdrop-blur-sm`}>
                <svg className={`w-6 h-6 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M2 6h20v2H2zm1.5 5h17c.8 0 1.5.7 1.5 1.5v8c0 .8-.7 1.5-1.5 1.5h-17c-.8 0-1.5-.7-1.5-1.5v-8c0-.8.7-1.5 1.5-1.5zm0 2v8h17v-8h-17zm2 2h3v4h-3v-4zm5-1h6v2h-6v-2zm0 3h4v2h-4v-2z"/>
                </svg>
              </div>
            </div>
            <h3 className={`text-xs sm:text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-3 leading-tight`}>
              Total Commission Earned
            </h3>
            <p className={`text-2xl sm:text-3xl font-bold ${darkMode ? 'text-purple-400' : 'text-purple-600'} break-words bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent`}>
              {formatCurrency(partnerData?.totalCommissionEarned)}
            </p>
          </div>

          <div className={`group p-6 sm:p-8 rounded-2xl ${
            darkMode 
              ? 'bg-gray-800/40 border border-gray-700/50' 
              : 'bg-white/30 border border-white/20'
          } backdrop-blur-xl shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-105 transform`}>
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl ${darkMode ? 'bg-orange-600/20' : 'bg-orange-500/20'} backdrop-blur-sm`}>
                <svg className={`w-6 h-6 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <h3 className={`text-xs sm:text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-3 leading-tight`}>
              Pending Commission
            </h3>
            <p className={`text-2xl sm:text-3xl font-bold ${darkMode ? 'text-orange-400' : 'text-orange-600'} break-words bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent`}>
              {formatCurrency(partnerData?.pendingCommission)}
            </p>
          </div>
        </div>

        {/* Student Tracking List */}
        <div className={`rounded-2xl ${
          darkMode 
            ? 'bg-gray-800/40 border border-gray-700/50' 
            : 'bg-white/30 border border-white/20'
        } backdrop-blur-xl shadow-2xl overflow-hidden`}>
          <div className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 border-b border-gray-200/20 dark:border-gray-700/30">
            <h2 className={`text-base sm:text-xl lg:text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'} break-words bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent`}>
              Student Referral Tracking
            </h2>
          </div>

          <div className="overflow-x-auto">
            <div className="space-y-4 sm:space-y-6 p-3 sm:p-6 lg:p-8">
              {referrals.length === 0 ? (
                <div className="text-center py-16">
                  <div className={`mx-auto w-20 h-20 rounded-2xl ${
                    darkMode 
                      ? 'bg-gray-700/30 border border-gray-600/30' 
                      : 'bg-white/40 border border-white/30'
                  } backdrop-blur-xl flex items-center justify-center mb-6 shadow-xl`}>
                    <svg className={`w-10 h-10 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className={`text-xl font-bold mb-4 ${darkMode ? 'text-gray-200' : 'text-gray-700'} bg-gradient-to-r from-gray-600 to-gray-800 bg-clip-text text-transparent`}>
                    No Student Referrals Yet
                  </h3>
                  <p className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-2 max-w-md mx-auto`}>
                    Students you refer will appear here. The admin will add your referrals to track their progress.
                  </p>
                  <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'} max-w-sm mx-auto`}>
                    Contact the admin to add your first student referral.
                  </p>
                </div>
              ) : (
                referrals.map((referral) => (
                <div
                  key={referral.id}
                  className={`group p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl ${
                    darkMode 
                      ? 'bg-gray-700/30 border border-gray-600/30' 
                      : 'bg-white/40 border border-white/30'
                  } backdrop-blur-xl shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] transform`}
                >
                  {/* Student Info Header */}
                  <div className="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                          <div className="flex items-center gap-2 sm:gap-3">
                            {/* Student Photo */}
                            <div className="flex-shrink-0">
                              {referral.studentPhoto ? (
                                <img 
                                  src={referral.studentPhoto} 
                                  alt={referral.studentName}
                                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-indigo-500/30 shadow-lg"
                                />
                              ) : (
                                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center ${darkMode ? 'bg-indigo-600/20 border-indigo-400/30' : 'bg-indigo-500/20 border-indigo-500/30'} border-2 backdrop-blur-sm`}>
                                  <svg className={`w-6 h-6 sm:w-7 sm:h-7 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          </div>
                          <h3 className={`text-base sm:text-lg lg:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'} break-words leading-tight`}>
                            {referral.studentName}
                          </h3>
                        </div>
                        <div className={`text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} space-y-1 sm:space-y-1.5 ml-0 sm:ml-16 lg:ml-16`}>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21L6.674 11.37a8.953 8.953 0 004.956 4.956l1.983-3.55a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              <span className="font-medium text-xs sm:text-sm">Phone:</span>
                            </div>
                            <span className="break-words text-xs sm:text-sm ml-5 sm:ml-0">{referral.studentPhone}</span>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <span className="font-medium text-xs sm:text-sm">Email:</span>
                            </div>
                            <span className="break-all text-xs sm:text-sm ml-5 sm:ml-0">{referral.studentEmail}</span>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V7a2 2 0 012-2h4a2 2 0 012 2v0M8 7h8m-9 4a5 5 0 0110 0 5 5 0 01-10 0z" />
                              </svg>
                              <span className="font-medium text-xs sm:text-sm">Referred:</span>
                            </div>
                            <span className="break-words text-xs sm:text-sm ml-5 sm:ml-0">{formatDateWithOrdinal(referral.referredAt)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0 mt-2 sm:mt-0">
                        <span className={`px-2 sm:px-3 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-semibold inline-block backdrop-blur-sm ${getStatusColor(referral.commissionStatus)}`}>
                          {referral.commissionStatus}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Progress Tracker */}
                  <div className="mb-3 sm:mb-4 lg:mb-6">
                    <h4 className={`text-xs sm:text-sm lg:text-base font-semibold mb-2 sm:mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'} break-words flex items-center gap-1 sm:gap-2`}>
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <span className="hidden sm:inline">Progress Status: {referral.currentStatus}</span>
                      <span className="sm:hidden">Progress: {referral.currentStatus}</span>
                    </h4>
                    <div className="px-1 sm:px-0">
                      <StatusProgressBar stages={referral.stages} />
                    </div>
                  </div>

                  {/* Commission Info */}
                  {referral.commissionStatus === 'Will be paid within 5-7 working days' && (
                    <div className={`mt-3 sm:mt-4 p-3 sm:p-4 lg:p-5 rounded-2xl relative overflow-hidden ${
                      darkMode 
                        ? 'bg-gradient-to-br from-green-900/40 via-emerald-900/30 to-green-800/40 border border-green-400/30' 
                        : 'bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 border border-green-300/40'
                    } backdrop-blur-xl shadow-xl transform hover:scale-[1.02] transition-all duration-300`}>
                      
                      {/* Professional Background Effects */}
                      <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        <div className="absolute top-2 right-2 w-6 h-6 sm:w-8 sm:h-8 bg-green-400/10 rounded-full animate-ping"></div>
                        <div className="absolute bottom-2 left-2 w-4 h-4 sm:w-6 sm:h-6 bg-emerald-400/10 rounded-full animate-pulse delay-500"></div>
                      </div>

                      <div className={`relative z-10 text-sm sm:text-base font-bold flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 ${
                        darkMode ? 'text-green-200' : 'text-green-900'
                      }`}>
                        <div className={`p-2 sm:p-3 rounded-2xl ${darkMode ? 'bg-gradient-to-br from-green-600/30 to-emerald-600/30' : 'bg-gradient-to-br from-green-500/20 to-emerald-500/20'} backdrop-blur-sm flex-shrink-0 self-start`}>
                          {/* Simple Tick Mark SVG - Fixed Size */}
                          <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/>
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="mb-1 sm:mb-2">
                            <span className="bg-gradient-to-r from-green-600 via-emerald-600 to-green-700 bg-clip-text text-transparent font-extrabold text-base sm:text-lg">
                              Commission Earned!
                            </span>
                          </div>
                          <div className={`text-xl sm:text-2xl font-black ${darkMode ? 'text-green-300' : 'text-green-700'} mb-1`}>
                            ₹20,000
                          </div>
                          <div className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
                            Will be paid within 5-7 working days
                          </div>
                        </div>
                      </div>
                      
                      {/* Payment Details */}
                      {partnerData?.paymentAccount && (() => {
                        const paymentDetails = maskPaymentDetails(partnerData.paymentAccount);
                        if (paymentDetails && paymentDetails.length > 0) {
                          return (
                            <div className="mt-3 sm:mt-4">
                              <div className={`text-xs sm:text-sm font-semibold mb-2 ${
                                darkMode ? 'text-green-200' : 'text-green-700'
                              }`}>
                                Your commission will be sent to:
                              </div>
                              <div className="space-y-1">
                                {paymentDetails.map((detail, index) => (
                                  <div key={index} className={`text-xs sm:text-sm font-medium break-all ${
                                    darkMode ? 'text-green-300' : 'text-green-600'
                                  }`}>
                                    {detail}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      
                      {referral.commissionEarnedAt && (
                        <div className={`text-xs sm:text-sm font-medium mt-3 sm:mt-4 break-all ${
                          darkMode ? 'text-green-300' : 'text-green-700'
                        }`}>
                          <span>Earned on: {formatDateTimeWithOrdinal(referral.commissionEarnedAt)}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {referral.commissionStatus === COMMISSION_STATUS.IN_PROGRESS && (
                    <div className={`mt-3 sm:mt-4 p-3 sm:p-4 rounded-xl ${
                      darkMode 
                        ? 'bg-blue-900/30 border border-blue-700/50' 
                        : 'bg-blue-50/80 border border-blue-200/50'
                    } backdrop-blur-xl shadow-md`}>
                      <p className={`text-xs sm:text-sm font-semibold flex flex-col sm:flex-row sm:items-center gap-2 ${
                        darkMode ? 'text-blue-300' : 'text-blue-800'
                      }`}>
                        <div className={`p-1.5 rounded-lg ${darkMode ? 'bg-blue-600/20' : 'bg-blue-500/20'} backdrop-blur-sm flex-shrink-0 self-start`}>
                          <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                        </div>
                        <span className="break-words bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent font-semibold">
                          Commission pending - ₹20,000 will be paid when student departs
                        </span>
                      </p>
                    </div>
                  )}
                </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerDashboardPage;
